// AI-Generated Code - 2025-02-22 - Crawl site dialog (commercial feature)

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Globe } from "lucide-react";

interface CrawlSiteDialogProps {
  children: React.ReactNode;
}

export function CrawlSiteDialog({ children }: CrawlSiteDialogProps) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [maxPages, setMaxPages] = useState(10);
  const [singleCrawl, setSingleCrawl] = useState(true);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const crawlMutation = useMutation({
    mutationFn: async (data: { url: string; maxPages: number; singleCrawl: boolean }) => {
      return apiRequest("/api/scans/crawl", { method: "POST", body: data });
    },
    onSuccess: (data: { scanId?: number; scanIds?: number[]; urlsDiscovered?: number; urlsQueued?: number; message?: string }) => {
      toast({
        title: "Crawl completed",
        description: data.message ?? (data.scanId != null
          ? `One scan (ID ${data.scanId}) with ${data.urlsDiscovered ?? 0} discovered URL(s).`
          : `${data.urlsQueued ?? 0} scan(s) started.`),
      });
      queryClient.invalidateQueries({ queryKey: ["/api/scans"] });
      setOpen(false);
      setUrl("");
      setMaxPages(10);
      setSingleCrawl(true);
    },
    onError: (error: Error) => {
      toast({
        title: "Crawl failed",
        description: error.message || "Failed to start site crawl",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = url?.trim();
    if (!trimmed) {
      toast({ title: "URL required", variant: "destructive" });
      return;
    }
    const validUrl = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const max = Math.min(Math.max(Number(maxPages) || 10, 1), 25);
    crawlMutation.mutate({ url: validUrl, maxPages: max, singleCrawl });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Crawl site
          </DialogTitle>
          <DialogDescription>
            Discover same-origin pages from the seed URL. Single crawl = one scan with the seed page and a list of all discovered URLs. Uncheck for one full scan per page (more entries in Recent Scans).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="crawl-url">Seed URL</Label>
              <Input
                id="crawl-url"
                placeholder="https://example.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="single-crawl"
                checked={singleCrawl}
                onChange={(e) => setSingleCrawl(e.target.checked)}
                className="rounded border-input"
              />
              <Label htmlFor="single-crawl" className="font-normal cursor-pointer">
                Single crawl (one scan, all discovered URLs in domain)
              </Label>
            </div>
            {!singleCrawl && (
            <div className="space-y-2">
              <Label htmlFor="max-pages">Max pages to scan (1–25)</Label>
              <Input
                id="max-pages"
                type="number"
                min={1}
                max={25}
                value={maxPages}
                onChange={(e) => setMaxPages(Number(e.target.value) || 10)}
              />
            </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={crawlMutation.isPending}>
              {crawlMutation.isPending ? "Running…" : singleCrawl ? "Start single crawl" : "Start crawl"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
